import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Printer } from 'lucide-react';
import { CLOSING_METHODS, A_PRAZO } from '../lib/closingMethods';
import { printHtmlSilently } from '../lib/silentPrint';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `R$ ${Number(v || 0).toFixed(2)}`;
const when = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

// Recebimentos por forma de pagamento do turno (do operador) — visível para o próprio operador ao encerrar
export const ShiftMethodSummary = ({ sessionId, title = 'Recebido no seu turno por forma de pagamento' }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [printMsg, setPrintMsg] = useState('');

  useEffect(() => {
    if (!sessionId) return;
    axios.get(`${API}/cash-sessions/${sessionId}/details`, { withCredentials: true })
      .then((r) => setD(r.data))
      .catch((e) => setErr(e.response?.data?.detail || 'Não foi possível carregar o resumo do turno'));
  }, [sessionId]);

  if (err) return <p className="text-xs text-red-600" data-testid="shift-summary-error">{err}</p>;
  if (!d) return <p className="text-xs text-slate-500" data-testid="shift-summary-loading">Carregando resumo do turno…</p>;

  // breakdown_expected.dinheiro é o caixa físico esperado (fundo + vendas − sangrias); aqui mostramos o RECEBIDO em vendas
  const bd = { ...(d.breakdown_expected || {}), dinheiro: Number(d.total_cash_sales || 0) };
  const rows = CLOSING_METHODS.filter((m) => m.always || Math.abs(Number(bd[m.key]) || 0) > 0.005);
  const received = rows.reduce((s, m) => s + (Number(bd[m.key]) || 0), 0);
  const aPrazo = Number(d.a_prazo_total || 0);

  const print = async () => {
    setPrintMsg('');
    const line = (l, r, b) => `<div class="row${b ? ' bold' : ''}"><span>${l}</span><span>${r}</span></div>`;
    const html = `<style>.row{display:flex;justify-content:space-between}.bold{font-weight:bold}h2{text-align:center;margin:4px 0}hr{border:0;border-top:1px dashed #000}</style>
      <h2>RESUMO DO TURNO</h2>
      <div class="row"><span>Operador</span><span>${d.user_name || '—'}</span></div>
      <div class="row"><span>Inicio</span><span>${when(d.opened_at)}</span></div>
      <div class="row"><span>Fim</span><span>${when(d.shift_ended_at || d.closed_at) || when(new Date().toISOString())}</span></div>
      <hr/><h2>RECEBIDO POR FORMA</h2>
      ${rows.map((m) => line(m.label.replace('(conta da loja)', 'loja'), money(bd[m.key]), false)).join('')}
      <hr/>${line('TOTAL RECEBIDO', money(received), true)}
      ${aPrazo > 0.005 ? line('A prazo (crediario)', money(aPrazo), false) : ''}
      <hr/>${line('Vendas', String(d.sales_count || 0), false)}${line('Fundo inicial', money(d.initial_amount), false)}
      ${Number(d.sangrias_total) > 0 ? line('Sangrias', '-' + money(d.sangrias_total), false) : ''}${Number(d.suprimentos_total) > 0 ? line('Suprimentos', '+' + money(d.suprimentos_total), false) : ''}
      ${line('Dinheiro esperado no caixa', money(d.expected_amount), true)}`;
    try { await printHtmlSilently(html); setPrintMsg('Resumo enviado para a impressora'); } catch (e) { setPrintMsg(e.message || 'Falha ao imprimir'); }
  };

  return (
    <div className="bg-white rounded-md border border-slate-200 overflow-hidden" data-testid="shift-method-summary">
      <div className="px-3 py-2 bg-slate-100 flex items-center justify-between gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-700">{title}</p>
          <p className="text-[11px] text-slate-500" data-testid="shift-summary-meta">{d.user_name || '—'} · {when(d.opened_at)} → {when(d.shift_ended_at || d.closed_at) === '—' ? 'agora' : when(d.shift_ended_at || d.closed_at)} · {d.sales_count || 0} venda(s)</p>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={print} className="h-8" data-testid="shift-summary-print-btn"><Printer size={14} className="mr-1" /> Imprimir</Button>
      </div>
      <table className="w-full text-sm">
        <tbody>
          {rows.map((m) => (
            <tr key={m.key} className="border-t border-slate-100" data-testid={`shift-summary-row-${m.key}`}>
              <td className="px-3 py-1.5 text-slate-700">{m.icon} {m.label}</td>
              <td className="px-3 py-1.5 text-right font-mono font-semibold" data-testid={`shift-summary-val-${m.key}`}>{money(bd[m.key])}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-slate-300 bg-emerald-50 font-black" data-testid="shift-summary-total-row">
            <td className="px-3 py-2">TOTAL RECEBIDO</td>
            <td className="px-3 py-2 text-right font-mono text-emerald-800" data-testid="shift-summary-total">{money(received)}</td>
          </tr>
          {aPrazo > 0.005 && (
            <tr className="border-t border-slate-100 text-slate-500" data-testid="shift-summary-row-a_prazo">
              <td className="px-3 py-1.5">{A_PRAZO.icon} {A_PRAZO.label} <span className="text-[10px]">(não entra no caixa)</span></td>
              <td className="px-3 py-1.5 text-right font-mono">{money(aPrazo)}</td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="px-3 py-2 text-[11px] text-slate-600 border-t border-slate-100 flex flex-wrap gap-x-4 gap-y-1" data-testid="shift-summary-cash-line">
        <span>Fundo inicial: <b>{money(d.initial_amount)}</b></span>
        {Number(d.sangrias_total) > 0 && <span>Sangrias: <b>−{money(d.sangrias_total)}</b></span>}
        {Number(d.suprimentos_total) > 0 && <span>Suprimentos: <b>+{money(d.suprimentos_total)}</b></span>}
        <span>Dinheiro esperado no caixa: <b>{money(d.expected_amount)}</b></span>
      </div>
      {printMsg && <p className="px-3 pb-2 text-[11px] text-slate-500" data-testid="shift-summary-print-msg">{printMsg}</p>}
    </div>
  );
};
